// ====================================================
// src/components/block/liveStreamManagement/EffectCarousel.tsx
//
// Grille "Effets audio"/"Effets vidéo" (SourceConfigModal) — et tout picker
// interne du même genre (cf. SurroundingsEffect) — à 3 colonnes visibles,
// flèches gauche/droite pour faire défiler le reste, et un bouton fixe
// "Ajouter un plugin" après la flèche droite (hors défilement — toujours
// accessible en un clic, quel que soit l'effet affiché).
//
// Les flèches sont toujours affichées (même désactivées, sans rien à faire
// défiler) : la rangée garde ainsi la même forme quel que soit le nombre
// d'effets, et les deux colonnes "Effets audio"/"Effets vidéo" s'alignent
// entre elles sans jamais casser la mise en page de la modale.
// ====================================================

import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

// Hauteur fixe, partagée par toutes les grilles d'effets : un badge "Bientôt" ou
// un engrenage de plus/moins sur un bouton ne doit jamais faire varier sa hauteur
// ni décaler l'icône/le libellé des autres boutons de la même rangée.
export const EFFECT_BUTTON_HEIGHT = 68;
export const EFFECT_VISIBLE_COUNT = 3;
export const EFFECT_GAP = 8;

const EFFECT_BASIS = `${100 / EFFECT_VISIBLE_COUNT}%`;
const EFFECT_STEP_GAP = EFFECT_GAP * (EFFECT_VISIBLE_COUNT - 1) / EFFECT_VISIBLE_COUNT;

export const EffectCarousel: React.FC<{ children: React.ReactNode[]; onAddPlugin?: () => void }> = ({ children, onAddPlugin }) => {
  const [index, setIndex] = useState(0);
  const total    = children.length;
  const maxIndex = Math.max(0, total - EFFECT_VISIBLE_COUNT);

  // Si le registry perd des effets (ou si `index` pointait trop loin après un
  // filtrage), on recale plutôt que de laisser une page vide à droite.
  useEffect(() => { setIndex(i => Math.min(i, maxIndex)); }, [maxIndex]);

  const arrowStyle = (disabled: boolean): React.CSSProperties => ({
    flexShrink: 0, width: 22, height: EFFECT_BUTTON_HEIGHT,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8,
    color: disabled ? 'rgba(148,163,184,0.3)' : '#cbd5e1',
    cursor: disabled ? 'default' : 'pointer', padding: 0,
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button onClick={() => setIndex(i => Math.max(0, i - 1))} disabled={index <= 0} style={arrowStyle(index <= 0)} title="Précédent">
        <ChevronLeft style={{ width: 14, height: 14 }} />
      </button>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', gap: EFFECT_GAP,
          transform: `translateX(calc(${-index} * (${EFFECT_BASIS} + ${EFFECT_STEP_GAP}px)))`,
          transition: 'transform 0.2s ease',
        }}>
          {children.map((child, i) => (
            <div key={i} style={{ flex: `0 0 calc(${EFFECT_BASIS} - ${EFFECT_STEP_GAP}px)`, minWidth: 0 }}>
              {child}
            </div>
          ))}
        </div>
      </div>
      <button onClick={() => setIndex(i => Math.min(maxIndex, i + 1))} disabled={index >= maxIndex} style={arrowStyle(index >= maxIndex)} title="Suivant">
        <ChevronRight style={{ width: 14, height: 14 }} />
      </button>
      <AddPluginButton onClick={onAddPlugin} />
    </div>
  );
};

// ── AddPluginButton ───────────────────────────────────────────────────────────
// Même gabarit que les boutons d'effet (icône + libellé, EFFECT_BUTTON_HEIGHT),
// mais hors défilement — toujours à la même place, à droite de la flèche droite.
// Pas encore branché à un système de plugins : grisé "Bientôt" comme les effets
// pas encore disponibles (cf. EffectButton/AudioEffectButton dans SourceConfigModal),
// en attendant que `onAddPlugin` ait quelque chose à faire.
const AddPluginButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <div
    role="button"
    aria-disabled={!onClick}
    tabIndex={onClick ? 0 : -1}
    onClick={onClick}
    onKeyDown={e => { if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
    title={onClick ? 'Ajouter un plugin' : 'Ajouter un plugin — bientôt disponible'}
    style={{
      flexShrink: 0, width: 68, height: EFFECT_BUTTON_HEIGHT, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '10px 4px', borderRadius: 10,
      background: '#1e293b', border: '2px dashed #ffffff',
      color: '#94a3b8',
      cursor: onClick ? 'pointer' : 'not-allowed',
      opacity: onClick ? 1 : 0.6,
      userSelect: 'none',
    }}
  >
    <Plus style={{ width: 18, height: 18 }} />
    <span style={{ fontSize: 9, fontWeight: 600, lineHeight: 1.2, textAlign: 'center' }}>Ajouter un plugin</span>
  </div>
);
